import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    Edit2,
    Save,
    HardHat,
    LogOut,
    LayoutGrid,
    Clipboard,
    Video,
    Image,
    Camera,
    FileText,
    MapPin,
    Package,
    StickyNote,
    Megaphone,
    X,
    CheckCircle2,
    Circle,
    Target,
    Play,
    Layers,
    CalendarDays,
    CheckSquare,
    Square,
    FileDown,
    List
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { postsAPI } from '../lib/api';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

// Helpers
const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const formatDate = (date) => date.toISOString().split('T')[0];

const getWeekDates = (year, week) => {
    const jan4 = new Date(year, 0, 4);
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
    const targetMonday = new Date(week1Monday);
    targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(targetMonday);
        d.setDate(targetMonday.getDate() + i);
        dates.push(d);
    }
    return dates;
};

const REDES = {
    instagram: { label: 'IG', full: 'Instagram', color: 'bg-[#E1306C]', text: 'text-[#E1306C]' },
    facebook: { label: 'FB', full: 'Facebook', color: 'bg-[#1877F2]', text: 'text-[#1877F2]' },
    linkedin: { label: 'LI', full: 'LinkedIn', color: 'bg-[#0A66C2]', text: 'text-[#0A66C2]' },
    tiktok: { label: 'TT', full: 'TikTok', color: 'bg-[#000000]', text: 'text-black' },
    twitter: { label: 'X', full: 'Twitter/X', color: 'bg-[#1DA1F2]', text: 'text-[#1DA1F2]' },
    multiple: { label: 'Multi', full: 'Múltiples', color: 'bg-[#6366F1]', text: 'text-[#6366F1]' }
};

const ESTADOS = {
    borrador: { label: 'Borrador', color: 'text-slate-500', bg: 'bg-slate-100', dot: 'bg-slate-400' },
    pendiente: { label: 'Pendiente', color: 'text-amber-600', bg: 'bg-amber-50', dot: 'bg-amber-500' },
    aprobado: { label: 'Aprobado', color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
    publicado: { label: 'Publicado', color: 'text-blue-600', bg: 'bg-blue-50', dot: 'bg-blue-500' }
};

const tipoIcons = { imagen: Image, video: Video, carrusel: Layers, reel: Play, story: Camera };

const getRedConfig = (red) => REDES[red] || REDES.instagram;
const getEstadoConfig = (estado) => ESTADOS[estado] || ESTADOS.borrador;

const ProduccionPage = () => {
    const { user, logout, isAdmin } = useAuth();
    const [searchParams] = useSearchParams();
    const highlightId = searchParams.get('highlight');
    const pdfRef = useRef(null);

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentWeek, setCurrentWeek] = useState(getWeekNumber(new Date()));
    const [expandedId, setExpandedId] = useState(highlightId || null);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    // Selection states
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedPosts, setSelectedPosts] = useState(new Set());
    const [generatingPDF, setGeneratingPDF] = useState(false);

    const weekDates = getWeekDates(currentYear, currentWeek);

    // Si hay highlightId, primero obtener el post para saber su fecha
    useEffect(() => {
        const loadHighlightedPost = async () => {
            if (highlightId && !initialLoadDone) {
                try {
                    // Intentar obtener el post específico
                    const response = await postsAPI.getById(highlightId);
                    if (response.data && response.data.fecha) {
                        const postDate = new Date(response.data.fecha + 'T12:00:00');
                        const postYear = postDate.getFullYear();
                        const postWeek = getWeekNumber(postDate);

                        // Cambiar a la semana del post
                        setCurrentYear(postYear);
                        setCurrentWeek(postWeek);
                    }
                } catch (error) {
                    console.log('Post not found, using current week');
                }
                setInitialLoadDone(true);
            } else if (!highlightId) {
                setInitialLoadDone(true);
            }
        };
        loadHighlightedPost();
    }, [highlightId]);

    useEffect(() => {
        if (initialLoadDone) {
            fetchPosts();
        }
    }, [currentYear, currentWeek, initialLoadDone]);

    useEffect(() => {
        if (highlightId && posts.length > 0) {
            setExpandedId(highlightId);
            setTimeout(() => {
                const element = document.getElementById(`post-${highlightId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);
        }
    }, [highlightId, posts]);

    const fetchPosts = async () => {
        setLoading(true);
        try {
            const startDate = formatDate(weekDates[0]);
            const endDate = formatDate(weekDates[6]);
            const response = await postsAPI.getAll({ fecha_inicio: startDate, fecha_fin: endDate });
            setPosts(response.data);
        } catch (error) {
            toast.error('Error al cargar');
        } finally {
            setLoading(false);
        }
    };

    const goToPrevWeek = () => {
        if (currentWeek === 1) { setCurrentYear(y => y - 1); setCurrentWeek(52); }
        else setCurrentWeek(w => w - 1);
    };

    const goToNextWeek = () => {
        if (currentWeek === 52) { setCurrentYear(y => y + 1); setCurrentWeek(1); }
        else setCurrentWeek(w => w + 1);
    };

    const goToToday = () => {
        setCurrentYear(new Date().getFullYear());
        setCurrentWeek(getWeekNumber(new Date()));
    };

    const toggleExpand = (postId) => {
        setExpandedId(expandedId === postId ? null : postId);
        if (editingId && editingId !== postId) {
            setEditingId(null);
            setEditData({});
        }
    };

    const startEditing = (post, e) => {
        e.stopPropagation();
        setEditingId(post.id);
        setEditData({
            produccion_necesaria: post.produccion_necesaria || '',
            guion: post.guion || '',
            ubicacion: post.ubicacion || '',
            materiales: post.materiales || '',
            notas: post.notas || ''
        });
    };

    const cancelEditing = () => { setEditingId(null); setEditData({}); };

    const saveEditing = async () => {
        try {
            await postsAPI.update(editingId, editData);
            toast.success('Guardado');
            setEditingId(null);
            fetchPosts();
        } catch (error) {
            toast.error('Error al guardar');
        }
    };

    const hasProductionData = (post) => {
        return post.produccion_necesaria || post.guion || post.ubicacion || post.materiales || post.notas;
    };

    // Selection functions
    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode);
        if (selectionMode) {
            setSelectedPosts(new Set());
        }
    };

    const togglePostSelection = (postId, e) => {
        e.stopPropagation();
        const newSelected = new Set(selectedPosts);
        if (newSelected.has(postId)) {
            newSelected.delete(postId);
        } else {
            newSelected.add(postId);
        }
        setSelectedPosts(newSelected);
    };

    const selectAll = () => {
        setSelectedPosts(new Set(posts.map(p => p.id)));
    };

    const clearSelection = () => {
        setSelectedPosts(new Set());
    };

    const getSelectedPostsData = () => {
        return posts.filter(p => selectedPosts.has(p.id));
    };

    const generatePDF = async () => {
        if (selectedPosts.size === 0) {
            toast.error('Selecciona al menos una publicación');
            return;
        }

        setGeneratingPDF(true);

        try {
            const selectedData = getSelectedPostsData();

            // Create PDF content
            const content = document.createElement('div');
            content.style.padding = '20px';
            content.style.fontFamily = 'Arial, sans-serif';
            content.style.fontSize = '12px';

            content.innerHTML = `
                <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #0B3D91; padding-bottom: 20px;">
                    <h1 style="color: #0B3D91; margin: 0; font-size: 24px;">SEGUMAX PLANNER</h1>
                    <p style="color: #666; margin: 5px 0 0 0;">Lista de Producción - ${selectedData.length} publicaciones</p>
                    <p style="color: #999; margin: 5px 0 0 0; font-size: 10px;">Generado: ${new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                
                ${selectedData.map((post, index) => {
                const redConfig = getRedConfig(post.red_social);
                const isVideo = post.tipo === 'video' || post.tipo === 'reel';

                return `
                        <div style="margin-bottom: 25px; page-break-inside: avoid; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
                            <div style="background: ${post.red_social === 'instagram' ? '#E1306C' : post.red_social === 'facebook' ? '#1877F2' : post.red_social === 'linkedin' ? '#0A66C2' : post.red_social === 'tiktok' ? '#000' : '#6366F1'}; color: white; padding: 10px 15px; display: flex; justify-content: space-between; align-items: center;">
                                <span style="font-weight: bold;">${redConfig.full}</span>
                                <span style="font-size: 11px;">${new Date(post.fecha + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            </div>
                            
                            <div style="padding: 15px;">
                                <h3 style="margin: 0 0 10px 0; color: #1e293b; font-size: 16px;">${index + 1}. ${post.titulo}</h3>
                                
                                ${post.objetivo ? `
                                    <div style="background: #fef3c7; border: 1px solid #fcd34d; border-radius: 4px; padding: 10px; margin-bottom: 10px;">
                                        <strong style="color: #92400e; font-size: 10px; text-transform: uppercase;">Objetivo:</strong>
                                        <p style="margin: 5px 0 0 0; color: #78350f;">${post.objetivo}</p>
                                    </div>
                                ` : ''}
                                
                                ${post.copy ? `
                                    <div style="margin-bottom: 10px;">
                                        <strong style="color: #64748b; font-size: 10px; text-transform: uppercase;">Copy:</strong>
                                        <p style="margin: 5px 0 0 0; color: #334155; white-space: pre-wrap;">${post.copy}</p>
                                    </div>
                                ` : ''}
                                
                                <div style="background: #f8fafc; border-radius: 4px; padding: 10px; margin-top: 10px;">
                                    <strong style="color: #0B3D91; font-size: 11px; text-transform: uppercase; display: block; margin-bottom: 8px;">Producción</strong>
                                    
                                    ${post.produccion_necesaria ? `
                                        <div style="margin-bottom: 8px;">
                                            <strong style="color: #64748b; font-size: 10px;">Qué se necesita:</strong>
                                            <p style="margin: 3px 0 0 0; color: #334155;">${post.produccion_necesaria}</p>
                                        </div>
                                    ` : ''}
                                    
                                    ${isVideo && post.guion ? `
                                        <div style="margin-bottom: 8px;">
                                            <strong style="color: #64748b; font-size: 10px;">Guión:</strong>
                                            <p style="margin: 3px 0 0 0; color: #334155; white-space: pre-wrap; font-family: monospace; background: white; padding: 8px; border-radius: 4px;">${post.guion}</p>
                                        </div>
                                    ` : ''}
                                    
                                    <div style="display: flex; gap: 20px; flex-wrap: wrap;">
                                        ${post.ubicacion ? `
                                            <div>
                                                <strong style="color: #059669; font-size: 10px;">📍 Ubicación:</strong>
                                                <p style="margin: 3px 0 0 0; color: #334155;">${post.ubicacion}</p>
                                            </div>
                                        ` : ''}
                                        
                                        ${post.materiales ? `
                                            <div>
                                                <strong style="color: #d97706; font-size: 10px;">📷 Materiales:</strong>
                                                <p style="margin: 3px 0 0 0; color: #334155;">${post.materiales}</p>
                                            </div>
                                        ` : ''}
                                    </div>
                                    
                                    ${post.notas ? `
                                        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #e2e8f0;">
                                            <strong style="color: #64748b; font-size: 10px;">Notas:</strong>
                                            <p style="margin: 3px 0 0 0; color: #334155;">${post.notas}</p>
                                        </div>
                                    ` : ''}
                                    
                                    ${!post.produccion_necesaria && !post.guion && !post.ubicacion && !post.materiales && !post.notas ? `
                                        <p style="color: #94a3b8; font-style: italic;">Sin detalles de producción</p>
                                    ` : ''}
                                </div>
                            </div>
                        </div>
                    `;
            }).join('')}
            `;

            const opt = {
                margin: 10,
                filename: `produccion_segumax_${new Date().toISOString().split('T')[0]}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };

            await html2pdf().set(opt).from(content).save();
            toast.success('PDF generado correctamente');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error al generar PDF');
        } finally {
            setGeneratingPDF(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            {/* Header */}
            <header className="bg-[#0B3D91] text-white shadow-lg sticky top-0 z-40">
                <div className="max-w-5xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#FF6B00] rounded flex items-center justify-center">
                                <HardHat className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide">Segumax</h1>
                                <p className="text-xs text-white/60 uppercase tracking-wider">Planner</p>
                            </div>
                        </div>

                        <nav className="flex items-center gap-1">
                            <Link to="/grilla" className="flex items-center gap-2 px-4 py-2 rounded text-white/70 hover:bg-white/10 text-sm font-medium transition-colors">
                                <LayoutGrid className="w-4 h-4" /> Semanal
                            </Link>
                            <Link to="/mensual" className="flex items-center gap-2 px-4 py-2 rounded text-white/70 hover:bg-white/10 text-sm font-medium transition-colors">
                                <CalendarDays className="w-4 h-4" /> Mensual
                            </Link>
                            <Link to="/produccion" className="flex items-center gap-2 px-4 py-2 rounded bg-white/20 text-white text-sm font-medium">
                                <Clipboard className="w-4 h-4" /> Producción
                            </Link>
                        </nav>

                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <p className="text-sm font-medium">{user?.name}</p>
                                <p className="text-xs text-white/60 uppercase">{isAdmin() ? 'Administrador' : 'Cliente'}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => { logout(); window.location.href = '/login'; }} className="text-white/70 hover:text-white hover:bg-white/10">
                                <LogOut className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-6">
                {/* Week Navigation */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6 sticky top-[72px] z-30">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button variant="outline" size="icon" onClick={goToPrevWeek} className="h-9 w-9" data-testid="btn-prev-week">
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <div className="text-center min-w-[180px]">
                                <p className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 uppercase">Semana {currentWeek}</p>
                                <p className="text-xs text-slate-500">
                                    {weekDates[0].toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} - {weekDates[6].toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                            </div>
                            <Button variant="outline" size="icon" onClick={goToNextWeek} className="h-9 w-9" data-testid="btn-next-week">
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={goToToday}>Hoy</Button>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant={selectionMode ? "default" : "outline"}
                                size="sm"
                                onClick={toggleSelectionMode}
                                className={selectionMode ? "bg-[#FF6B00] hover:bg-[#e65000]" : ""}
                                data-testid="btn-selection-mode"
                            >
                                <List className="w-4 h-4 mr-1" />
                                {selectionMode ? 'Cancelar' : 'Seleccionar'}
                            </Button>
                            <span className="text-sm text-slate-500">
                                {posts.length} publicaciones
                            </span>
                        </div>
                    </div>
                </div>

                {/* Selection Bar */}
                {selectionMode && (
                    <div className="bg-[#0B3D91] text-white rounded-xl shadow-sm p-4 mb-6 sticky top-[140px] z-20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <span className="font-semibold">
                                    {selectedPosts.size} seleccionadas
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={selectAll}
                                    className="text-white/70 hover:text-white hover:bg-white/10"
                                >
                                    Seleccionar todas
                                </Button>
                                {selectedPosts.size > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearSelection}
                                        className="text-white/70 hover:text-white hover:bg-white/10"
                                    >
                                        Limpiar
                                    </Button>
                                )}
                            </div>
                            <Button
                                onClick={generatePDF}
                                disabled={selectedPosts.size === 0 || generatingPDF}
                                className="bg-[#FF6B00] hover:bg-[#e65000]"
                                data-testid="btn-generate-pdf"
                            >
                                <FileDown className="w-4 h-4 mr-2" />
                                {generatingPDF ? 'Generando...' : 'Generar PDF'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Posts List */}
                {loading ? (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <div className="w-8 h-8 border-4 border-[#0B3D91] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-slate-500">Cargando...</p>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <Clipboard className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">No hay publicaciones esta semana</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {posts.map((post) => {
                            const redConfig = getRedConfig(post.red_social);
                            const estadoConfig = getEstadoConfig(post.estado);
                            const TipoIcon = tipoIcons[post.tipo] || FileText;
                            const isExpanded = expandedId === post.id;
                            const isEditing = editingId === post.id;
                            const isVideo = post.tipo === 'video' || post.tipo === 'reel';
                            const isHighlighted = highlightId === post.id;
                            const hasProduction = hasProductionData(post);

                            return (
                                <div
                                    key={post.id}
                                    id={`post-${post.id}`}
                                    className={`bg-white rounded-lg border overflow-hidden transition-all duration-300 ${isHighlighted ? 'border-[#FF6B00] ring-2 ring-[#FF6B00]/20' : 'border-slate-200'
                                        } ${isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow'}`}
                                    data-testid={`produccion-post-${post.id}`}
                                >
                                    {/* Collapsed Header - Always visible */}
                                    <div
                                        onClick={() => !selectionMode && toggleExpand(post.id)}
                                        className={`flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50 border-b border-slate-200' : 'hover:bg-slate-50'
                                            } ${selectedPosts.has(post.id) ? 'bg-blue-50' : ''}`}
                                    >
                                        {/* Selection Checkbox */}
                                        {selectionMode && (
                                            <div
                                                onClick={(e) => togglePostSelection(post.id, e)}
                                                className="flex items-center justify-center"
                                            >
                                                <Checkbox
                                                    checked={selectedPosts.has(post.id)}
                                                    className="h-5 w-5"
                                                    data-testid={`checkbox-${post.id}`}
                                                />
                                            </div>
                                        )}

                                        {/* Red Social Badge */}
                                        <span className={`${redConfig.color} text-white px-2 py-1 rounded text-xs font-bold min-w-[40px] text-center`}>
                                            {redConfig.label}
                                        </span>

                                        {/* Date */}
                                        <div className="text-center min-w-[50px]">
                                            <p className="text-lg font-bold text-slate-700 leading-none">
                                                {new Date(post.fecha + 'T12:00:00').getDate()}
                                            </p>
                                            <p className="text-[10px] text-slate-400 uppercase">
                                                {new Date(post.fecha + 'T12:00:00').toLocaleDateString('es-AR', { month: 'short' })}
                                            </p>
                                        </div>

                                        {/* Title & Type */}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-slate-800 truncate">{post.titulo}</p>
                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                <TipoIcon className="w-3 h-3" />
                                                <span className="capitalize">{post.tipo}</span>
                                                {post.objetivo && (
                                                    <>
                                                        <span className="text-slate-300">•</span>
                                                        <span className="truncate max-w-[200px]">{post.objetivo}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Status indicators */}
                                        <div className="flex items-center gap-2">
                                            {post.promocionado && (
                                                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[10px] font-bold">ADS</span>
                                            )}
                                            {hasProduction && (
                                                <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3" /> Prod
                                                </span>
                                            )}
                                            <span className={`${estadoConfig.dot} w-2 h-2 rounded-full`}></span>
                                            <span className={`text-xs font-medium ${estadoConfig.color}`}>{estadoConfig.label}</span>
                                        </div>

                                        {/* Expand icon */}
                                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>

                                    {/* Expanded Content */}
                                    {isExpanded && (
                                        <div className="p-4 bg-white">
                                            {/* Objetivo */}
                                            {post.objetivo && (
                                                <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg p-3">
                                                    <Target className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-0.5">Objetivo</p>
                                                        <p className="text-slate-700 text-sm">{post.objetivo}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Production Section */}
                                            <div className="bg-slate-50 rounded-lg p-4">
                                                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                                                    <Package className="w-4 h-4 text-[#0B3D91]" />
                                                    Producción
                                                </h4>

                                                {isEditing ? (
                                                    /* Edit Mode */
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-500 mb-1">¿Qué se necesita?</label>
                                                            <Textarea
                                                                value={editData.produccion_necesaria}
                                                                onChange={(e) => setEditData({ ...editData, produccion_necesaria: e.target.value })}
                                                                placeholder="Equipos, personas, permisos..."
                                                                rows={2}
                                                                className="text-sm"
                                                                data-testid="input-produccion"
                                                            />
                                                        </div>

                                                        {isVideo && (
                                                            <div>
                                                                <label className="block text-xs font-medium text-slate-500 mb-1">Guión</label>
                                                                <Textarea
                                                                    value={editData.guion}
                                                                    onChange={(e) => setEditData({ ...editData, guion: e.target.value })}
                                                                    placeholder="Intro... Desarrollo... Cierre..."
                                                                    rows={4}
                                                                    className="font-mono text-sm"
                                                                    data-testid="input-guion"
                                                                />
                                                            </div>
                                                        )}

                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                <label className="block text-xs font-medium text-slate-500 mb-1">Ubicación</label>
                                                                <Input value={editData.ubicacion} onChange={(e) => setEditData({ ...editData, ubicacion: e.target.value })} placeholder="¿Dónde?" className="text-sm" data-testid="input-ubicacion" />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium text-slate-500 mb-1">Materiales</label>
                                                                <Input value={editData.materiales} onChange={(e) => setEditData({ ...editData, materiales: e.target.value })} placeholder="Props..." className="text-sm" data-testid="input-materiales" />
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-500 mb-1">Notas</label>
                                                            <Textarea value={editData.notas} onChange={(e) => setEditData({ ...editData, notas: e.target.value })} placeholder="Notas..." rows={2} className="text-sm" data-testid="input-notas" />
                                                        </div>

                                                        <div className="flex gap-2 pt-2">
                                                            <Button variant="outline" size="sm" onClick={cancelEditing} className="flex-1">
                                                                <X className="w-4 h-4 mr-1" /> Cancelar
                                                            </Button>
                                                            <Button size="sm" onClick={saveEditing} className="flex-1 bg-[#0B3D91] hover:bg-[#0B3D91]/90" data-testid="btn-save-produccion">
                                                                <Save className="w-4 h-4 mr-1" /> Guardar
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : hasProduction ? (
                                                    /* View Mode with data */
                                                    <div className="space-y-3">
                                                        {post.produccion_necesaria && (
                                                            <div className="bg-white rounded border border-slate-200 p-3">
                                                                <p className="text-xs font-semibold text-[#0B3D91] uppercase mb-1">Qué se necesita</p>
                                                                <p className="text-sm text-slate-700">{post.produccion_necesaria}</p>
                                                            </div>
                                                        )}

                                                        {isVideo && post.guion && (
                                                            <div className="bg-white rounded border border-slate-200 p-3">
                                                                <p className="text-xs font-semibold text-[#0B3D91] uppercase mb-1">Guión</p>
                                                                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{post.guion}</pre>
                                                            </div>
                                                        )}

                                                        {(post.ubicacion || post.materiales) && (
                                                            <div className="grid grid-cols-2 gap-3">
                                                                {post.ubicacion && (
                                                                    <div className="bg-white rounded border border-slate-200 p-3">
                                                                        <p className="text-xs font-semibold text-emerald-600 uppercase mb-1 flex items-center gap-1">
                                                                            <MapPin className="w-3 h-3" /> Ubicación
                                                                        </p>
                                                                        <p className="text-sm text-slate-700">{post.ubicacion}</p>
                                                                    </div>
                                                                )}
                                                                {post.materiales && (
                                                                    <div className="bg-white rounded border border-slate-200 p-3">
                                                                        <p className="text-xs font-semibold text-amber-600 uppercase mb-1 flex items-center gap-1">
                                                                            <Camera className="w-3 h-3" /> Materiales
                                                                        </p>
                                                                        <p className="text-sm text-slate-700">{post.materiales}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {post.notas && (
                                                            <div className="bg-white rounded border border-slate-200 p-3">
                                                                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Notas</p>
                                                                <p className="text-sm text-slate-700">{post.notas}</p>
                                                            </div>
                                                        )}

                                                        {isAdmin() && (
                                                            <Button variant="outline" size="sm" onClick={(e) => startEditing(post, e)} className="mt-2" data-testid={`btn-edit-${post.id}`}>
                                                                <Edit2 className="w-4 h-4 mr-1" /> Editar
                                                            </Button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    /* Empty state */
                                                    <div className="text-center py-4">
                                                        <p className="text-sm text-slate-400 mb-2">Sin detalles de producción</p>
                                                        {isAdmin() && (
                                                            <Button variant="outline" size="sm" onClick={(e) => startEditing(post, e)} data-testid={`btn-edit-${post.id}`}>
                                                                <Edit2 className="w-4 h-4 mr-1" /> Agregar
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
};

export default ProduccionPage;
